import React, { useState, useMemo } from 'react';
import { EventOption } from '../types';
import {
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { isDeadlinePassed, getDeadlineStatus, getEventRegistrationStatus, EventRegistrationStatus } from '../utils/dateUtils';

interface EventScheduleProps {
  events: EventOption[];
  year: number;
  onRegisterClick: (eventId?: string, year?: number) => void;
}

// Get registration status badge configuration with better color scheme
const getRegistrationStatusConfig = (registrationStatus: EventRegistrationStatus) => {
  switch (registrationStatus) {
    case 'accepting':
      return {
        label: '報名中',
        bgColor: 'bg-teal-700',
        textColor: 'text-teal-800',
        lightBg: 'bg-teal-50'
      };
    case 'upcoming':
      return {
        label: '即將開始',
        bgColor: 'bg-yellow-600',
        textColor: 'text-yellow-800',
        lightBg: 'bg-yellow-50'
      };
    case 'full':
      return {
        label: '已額滿',
        bgColor: 'bg-orange-600',
        textColor: 'text-orange-800',
        lightBg: 'bg-orange-50'
      };
    case 'ended':
      return {
        label: '已結束',
        bgColor: 'bg-gray-500',
        textColor: 'text-gray-700',
        lightBg: 'bg-gray-50'
      };
  }
};

// Event Card Component - Clean design with neutral colors
const EventCard: React.FC<{ event: EventOption; onRegisterClick: (eventId: string) => void }> = ({ event, onRegisterClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const registrationStatus = getEventRegistrationStatus(event);
  const statusConfig = getRegistrationStatusConfig(registrationStatus);
  const hasSchedule = event.schedule && event.schedule.length > 0;
  const isPassed = isDeadlinePassed(event);
  const deadlineStatus = getDeadlineStatus(event);

  return (
    <div
      className={`rounded-2xl transition-all duration-300 overflow-hidden flex flex-col border border-gray-200 hover:shadow-lg bg-white ${
        registrationStatus === 'ended' || registrationStatus === 'full' ? 'opacity-70 bg-gray-50' : ''
      }`}
    >
      {/* Header - Clean color bar with dynamic status */}
      <div className={`${statusConfig.bgColor} px-6 py-4 transition-all duration-300 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300"></div>
        <div className="text-xs font-semibold text-white/90 mb-1 relative z-10">
          {statusConfig.label}
        </div>
        <h3 className="text-xl font-bold text-white relative z-10">{event.dateRange}</h3>
      </div>

      {/* Main Content - Clean typography */}
      <div className="p-6 flex-grow flex flex-col space-y-4">
        {/* Location - Simple text, no icon */}
        <div>
          <div className="text-lg font-semibold text-gray-800">{event.location}</div>
          {event.description && (
            <div className="text-sm text-gray-600 mt-1">{event.description}</div>
          )}
        </div>

        {/* Schedule Section - Expandable, minimal design */}
        {hasSchedule && (
          <div className="border-t border-gray-200/50 pt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-between text-indigo-700 hover:text-indigo-800 font-medium transition-colors duration-200 hover:bg-indigo-50 p-2 rounded-lg"
            >
              <span className="font-semibold">詳細行程</span>
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>

            {/* Expanded Schedule - Clean list design */}
            {isExpanded && (
              <div className="mt-4 space-y-3 animate-slide-fade-in-left">
                {event.schedule!.map((item, idx) => (
                  <div
                    key={idx}
                    className="pl-4 border-l-2 border-gray-300/50 hover:border-indigo-400 transition-all duration-300 hover:pl-5 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    {/* Date */}
                    <div className="text-sm font-semibold text-gray-800 mb-1">
                      {item.date}
                    </div>

                    {/* Location and Activity - Clean text layout */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-grow">
                        <div className="font-medium text-gray-700">
                          {item.location}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {item.timeOfDay} · {item.activity}
                        </div>
                        {item.notes && (
                          <div className="text-xs text-gray-500 italic mt-1">
                            {item.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Deadline - Status-aware display */}
        <div className={`flex items-center justify-between text-sm border-t border-gray-200/50 pt-4 ${hasSchedule ? '' : 'mt-auto'}`}>
          <div className="text-gray-600">
            <span>報名截止：
              <span className={`font-semibold ml-1 ${
                isPassed ? 'text-gray-500' :
                deadlineStatus.type === 'urgent' ? 'text-red-600' :
                'text-red-500'
              }`}>
                {event.deadline}
              </span>
            </span>
          </div>

          {isPassed && (
            <span className="text-xs font-semibold px-2 py-1 rounded bg-gray-200 text-gray-600">
              已截止
            </span>
          )}
        </div>

        {/* Register Button - Enhanced micro-interactions */}
        {registrationStatus === 'accepting' && (
          <div className="mt-4">
            <button
              onClick={() => onRegisterClick(event.id)}
              className="group w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <span>{event.type === 'assembly' ? '報名此聚會' : '報名此梯次'}</span>
              <svg
                className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component - Clean and professional with enhanced three-status filtering
const EventSchedule: React.FC<EventScheduleProps> = ({ events, year, onRegisterClick }) => {
  // Compute available years from events data
  const availableYears = useMemo(() => {
    const years = [...new Set(events.map(e => e.year || 2025))].sort();
    return years.length > 0 ? years : [year];
  }, [events, year]);

  // Smart default: if current calendar year's last event endDate has passed, default to next year
  const smartDefaultYear = useMemo(() => {
    const now = new Date();
    const currentCalendarYear = now.getFullYear();
    const currentYearEvents = events.filter(e => (e.year || 2025) === currentCalendarYear);

    if (currentYearEvents.length > 0) {
      const lastEndDate = currentYearEvents
        .map(e => e.endDate)
        .filter((d): d is string => !!d)
        .sort()
        .pop();
      if (lastEndDate) {
        const parsed = new Date(`${lastEndDate.trim().replace(/\//g, '-')}T23:59:59`);
        if (!Number.isNaN(parsed.getTime()) && now > parsed) {
          const nextYear = currentCalendarYear + 1;
          if (availableYears.includes(nextYear)) return nextYear;
        }
      }
    }
    return availableYears.includes(currentCalendarYear) ? currentCalendarYear : availableYears[0];
  }, [events, availableYears]);

  const [selectedYear, setSelectedYear] = useState<number>(smartDefaultYear);
  // If selectedYear is no longer in available years (e.g. events reloaded), reset
  const effectiveYear = availableYears.includes(selectedYear) ? selectedYear : smartDefaultYear;

  // Filter events by selected year tab
  const yearEvents = events.filter(e => (e.year || 2025) === effectiveYear);

  // Enhanced filter options with three states
  const [filterStatus, setFilterStatus] = useState<EventRegistrationStatus | 'all'>('accepting');

  // Filter events based on registration status, sorted by startDate
  const filteredEvents = yearEvents
    .filter(evt => {
      if (filterStatus === 'all') return true;
      return getEventRegistrationStatus(evt) === filterStatus;
    })
    .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));

  // Count events by status
  const statusCounts = {
    accepting: yearEvents.filter(e => getEventRegistrationStatus(e) === 'accepting').length,
    full: yearEvents.filter(e => getEventRegistrationStatus(e) === 'full').length,
    upcoming: yearEvents.filter(e => getEventRegistrationStatus(e) === 'upcoming').length,
    ended: yearEvents.filter(e => getEventRegistrationStatus(e) === 'ended').length,
  };

  return (
    <div className="mx-auto max-w-6xl px-3 pt-4 pb-8 sm:px-4 md:py-8">
      {/* Year Tabs - only show when multiple years exist */}
      {availableYears.length > 1 && (
        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-xl bg-gray-100 p-1 gap-1">
            {availableYears.map(y => (
              <button
                key={y}
                onClick={() => { setSelectedYear(y); setFilterStatus('accepting'); }}
                className={`rounded-lg px-5 py-2 text-sm font-bold transition-all duration-200 ${
                  effectiveYear === y
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Header - Clean typography */}
      <div className="mb-8 text-center md:mb-10">
        <h2 className="mb-3 text-2xl font-bold leading-tight text-gray-800 sm:text-3xl md:text-4xl">
          {effectiveYear} 年度醫宣梯隊計畫表
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-gray-700 sm:text-base md:mb-8">
          「要收的莊稼多，做工的人少。」<span className="text-gray-500">（路加福音 10:2）</span>
        </p>

        {/* Enhanced Filter Buttons with three status categories */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center md:gap-3">
          <button
            onClick={() => setFilterStatus('accepting')}
            className={`flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 sm:px-5 ${
              filterStatus === 'accepting'
                ? 'bg-teal-50 text-teal-800 shadow-sm ring-1 ring-teal-200'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            報名中 <span className={`ml-1.5 rounded-full px-2 py-0.5 text-xs ${filterStatus === 'accepting' ? 'bg-teal-700 text-white' : 'bg-gray-200 text-gray-600'}`}>{statusCounts.accepting}</span>
          </button>

          {statusCounts.full > 0 && (
            <button
              onClick={() => setFilterStatus('full')}
              className={`flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 sm:px-5 ${
                filterStatus === 'full'
                  ? 'bg-orange-50 text-orange-800 shadow-sm ring-1 ring-orange-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              已額滿 <span className={`ml-1.5 rounded-full px-2 py-0.5 text-xs ${filterStatus === 'full' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{statusCounts.full}</span>
            </button>
          )}

          <button
            onClick={() => setFilterStatus('upcoming')}
            className={`flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 sm:px-5 ${
              filterStatus === 'upcoming'
                ? 'bg-yellow-50 text-yellow-800 shadow-sm ring-1 ring-yellow-200'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            即將開始 <span className={`ml-1.5 rounded-full px-2 py-0.5 text-xs ${filterStatus === 'upcoming' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{statusCounts.upcoming}</span>
          </button>

          <button
            onClick={() => setFilterStatus('ended')}
            className={`flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 sm:px-5 ${
              filterStatus === 'ended'
                ? 'bg-gray-100 text-gray-700 shadow-sm ring-1 ring-gray-300'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            已結束 <span className={`ml-1.5 rounded-full px-2 py-0.5 text-xs ${filterStatus === 'ended' ? 'bg-gray-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{statusCounts.ended}</span>
          </button>
        </div>
      </div>

      {/* Events Grid - Clean layout with enhanced empty state */}
      {filteredEvents.length === 0 ? (
        <div className="p-12 rounded-2xl text-center bg-gray-50 border border-gray-200">
          <div className="text-4xl mb-3">📅</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-1">
            {filterStatus === 'accepting' && '目前沒有開放報名的梯隊'}
            {filterStatus === 'full' && '目前沒有已額滿的梯隊'}
            {filterStatus === 'upcoming' && '目前沒有即將開始的梯隊'}
            {filterStatus === 'ended' && '沒有已結束的梯隊'}
          </h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((evt) => (
            <EventCard key={evt.id} event={evt} onRegisterClick={(eventId) => onRegisterClick(eventId, effectiveYear)} />
          ))}
        </div>
      )}

      {/* Register Button - Strong Call-to-Action */}
      <div className="mt-12 text-center">
        <button
          onClick={() => onRegisterClick(undefined, effectiveYear)}
          className="group px-16 py-5 rounded-2xl text-2xl font-bold text-white transition-all duration-200 shadow-xl hover:shadow-2xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98]"
        >
          <span className="flex items-center justify-center gap-3">
            ✒️ 立即填寫報名表
            <svg 
              className="w-6 h-6 transition-transform duration-200 group-hover:translate-x-1" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default EventSchedule;
